import Title from "common/Typography/Title";
import {
  FieldName,
  SecurityConfigurationCard,
  ValueAndAction,
  ValueAndActionWrapper,
} from "features/settings/common/styles";
import Text from "common/Typography/Text";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import StatusTag from "common/Tags/StatusTag";
import ConnectExternalAccountModal from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountModal";
import {
  AuthUser,
  AnalyticsPageName,
  ExternalAccountType,
  UserSource,
  LanguageNameSpaces,
} from "@shared/types";
import useConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/useConnectTelegram";
import Link from "common/Typography/Link";
import {
  displayResultMessage,
  getOnboardingUserSource,
  removeLocalStorageSignalRefKey,
} from "utils/misc";
import { getExternalAccountDisplay } from "@shared/utils/misc";
import useQuery from "utils/hooks/useQuery";
import { getSignalFollowData } from "utils/routes";
import { getUserSourceFromRef } from "@shared/features/onboarding/utils";
import { sharedLib } from "@shared/services/shared.service";
import useConfirmModal from "common/Modal/useConfirmModal";
import useDisconnectExternalAccount from "@shared/features/settings/hooks/useDisconnectExternalAccount";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Tooltip from "common/Tooltips/Tooltip";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { Check } from "lucide-react";
import { Pill } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import { Discord, Telegram } from "common/Icons/Icons";
import SettingsRowLabel from "features/settings/NewLook/SettingsRowLabel.tsx";

const ExternalAccountsSection = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const query = useQuery();
  const { ref } = getSignalFollowData(query);
  const userSource = ref
    ? getUserSourceFromRef(ref)
    : getOnboardingUserSource();

  const [externalAccountModalVisible, setExternalAccountModalVisible] =
    useState(false);
  const [externalAccountType, setExternalAccountType] = useState<
    ExternalAccountType | undefined
  >();

  const { onDisconnectClick, confirmModalContextHolder } =
    useDisconnectExternalAccount({ useConfirmModal, displayResultMessage });

  useEffect(() => {
    if (userSource === UserSource.DISCORD)
      setExternalAccountType(ExternalAccountType.DISCORD);
    else if (userSource === UserSource.TELEGRAM)
      setExternalAccountType(ExternalAccountType.TELEGRAM);
    if (userSource) removeLocalStorageSignalRefKey();
  }, [userSource]);

  const { queryCode, queryUuid } = useConnectTelegram(
    externalAccountType,
    setExternalAccountType,
    setExternalAccountModalVisible
  );

  const closeModal = () => {
    setExternalAccountType(undefined);
    setExternalAccountModalVisible(false);
  };

  const infoPopover = (
    <HelperTooltip
      analyticsFieldName={t("external_accounts.field_name")}
      analyticsPageName={AnalyticsPageName.ACCOUNT_AND_SECURITY}
      description={t("external_accounts.description")}
      containerStyle={appTheme.isNewLook ? undefined : { marginLeft: 8 }}
    />
  );

  const connectedTag = (isConnected?: boolean) => {
    if (appTheme.isNewLook) {
      return isConnected ? (
        <Pill tone="green" icon={Check}>
          {t("external_accounts.connected")}
        </Pill>
      ) : (
        <Pill>{t("external_accounts.not_connected")}</Pill>
      );
    }
    return (
      <StatusTag
        color={isConnected ? appTheme.colors.green : appTheme.colors.red}
        text={
          isConnected
            ? t("external_accounts.connected")
            : t("external_accounts.not_connected")
        }
        style={isBottomNavigator ? { padding: "2px 8px" } : undefined}
      />
    );
  };

  const getStatusComponent = (accountType: ExternalAccountType) => {
    const { isConnected, text, tooltipDescription } = getExternalAccountDisplay(
      user,
      accountType
    );

    return (
      <StatusWrapper>
        {connectedTag(isConnected)}
        {text && (
          <Tooltip title={tooltipDescription} placement="bottom">
            <StatusText
              as="span"
              type="secondary"
              $ellipsis={true}
              $size={
                isBottomNavigator
                  ? typography.fontSizes.S
                  : typography.fontSizes.M
              }
            >
              {text}
            </StatusText>
          </Tooltip>
        )}
      </StatusWrapper>
    );
  };

  const getExternalAccountRow = (externalAccount: ExternalAccountType) => {
    const isConnected = user?.[EXTERNAL_ACCOUNT_TO_KEY[externalAccount]];
    return (
      <ValueAndActionWrapper>
        <FieldName>
          {appTheme.isNewLook ? (
            <SettingsRowLabel
              icon={
                externalAccount === ExternalAccountType.TELEGRAM ? (
                  <Telegram />
                ) : (
                  <Discord />
                )
              }
            >
              {externalAccount}
            </SettingsRowLabel>
          ) : (
            externalAccount
          )}
        </FieldName>
        <ValueAndAction style={{ overflow: "hidden" }}>
          {getStatusComponent(externalAccount)}
          {!isConnected && (
            <Link onClick={() => setExternalAccountType(externalAccount)}>
              {t("external_accounts.connect")}
            </Link>
          )}
          {isConnected && !!user?.email && (
            <Link onClick={() => onDisconnectClick(externalAccount)}>
              {t("external_accounts.disconnect")}
            </Link>
          )}
        </ValueAndAction>
      </ValueAndActionWrapper>
    );
  };

  const modals = (
    <>
      <ConnectExternalAccountModal
        visible={externalAccountModalVisible}
        externalAccountType={externalAccountType}
        onCancel={closeModal}
        uuid={queryUuid}
        code={queryCode}
      />
      {confirmModalContextHolder}
    </>
  );

  if (appTheme.isNewLook) {
    return (
      <Panel title={t("external_accounts.title")} extra={infoPopover} flush>
        {getExternalAccountRow(ExternalAccountType.TELEGRAM)}
        {getExternalAccountRow(ExternalAccountType.DISCORD)}
        {modals}
      </Panel>
    );
  }

  return (
    <SecurityConfigurationCard>
      <Title level={3} style={{ display: "flex" }}>
        {t("external_accounts.title")} {infoPopover}
      </Title>
      {getExternalAccountRow(ExternalAccountType.TELEGRAM)}
      {getExternalAccountRow(ExternalAccountType.DISCORD)}
      <ConnectExternalAccountModal
        visible={externalAccountModalVisible}
        externalAccountType={externalAccountType}
        onCancel={closeModal}
        uuid={queryUuid}
        code={queryCode}
      />
      {confirmModalContextHolder}
    </SecurityConfigurationCard>
  );
};

export default ExternalAccountsSection;

const EXTERNAL_ACCOUNT_TO_KEY: Record<ExternalAccountType, keyof AuthUser> = {
  [ExternalAccountType.TELEGRAM]: "is_telegram_connected",
  [ExternalAccountType.DISCORD]: "is_discord_connected",
};

const STATUS_BREAKOUT = 650;
const StatusWrapper = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  @media (max-width: ${STATUS_BREAKOUT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const StatusText = styled(Text).attrs({ type: "secondary" })`
  @media (min-width: ${STATUS_BREAKOUT}px) {
    margin: 0 12px;
  }
  @media (max-width: ${STATUS_BREAKOUT}px) {
    margin-top: 4px;
    padding-right: 12px;
  }
`;
